import { Box, CircularProgress, SwipeableDrawer } from '@material-ui/core';
import moment from 'moment';
import React, { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import './font.css';

import NotFoundPage from '../../../../pages/NotFoundPage';
import { useRestaurant, useRestaurantId } from '../../../../selectors/RestaurantSelectors';
import * as Booking from '../../../entities/booking';
import * as Orders from '../../../entities/orders';
import { useGetBookingRequestRange } from '../../../entities/reservesSchema/lib/useGetBookingRequestRange';
import { useResponsiveFlags } from '../../../shared/lib';
import { useDebounce } from '../../../shared/lib/debounce';
import { useSections } from '../../../widgets/reservesSchemas';
import {
  // getCancelConfirmationMessages,
  // getGuestLeftConfirmationMessage,
  getReserveItemTimeStatus,
  isTableOccupied,
  URI,
  isTableOpen,
  getReserveItemState,
} from '../lib';
import { lightTheme } from '../lib/theme';
import { CalendarWidget } from './calendarWidget';
import { ConfirmationBottomSheet } from './confirmationBottomSheet';
import { HR } from './hr';
import { LegendBottomSheet } from './legendBottomSheet';
import { Modal } from './modal';
import { OpenedReserveWidget } from './openedReserveWidget';
import { ReserveInfo } from './reserveInfo';
import { ReservesHorizontalList } from './reservesHorizontalList';
import { ReservesListContainer, ReservesListContainerRef } from './reservesListContainer';
import { ReserveWidget } from './reserveWidget';
import { SchemaContainerRef } from './schemaContainer';
import { SchemaWidget } from './schemaWidget';
import { TableWidget } from './tableWidget';

const { fg } = lightTheme;
const { button } = lightTheme.components;

export const ReservesSchemas = () => {
  const { isLargeScreen, isMobile, isMobilePortrait } = useResponsiveFlags();

  // // Логика ширины правой колонки:
  // // - Меньше 1100px: 280px (было 260)
  // // - От 1100px до 1280px: 320px
  // // - От 1280px до 1400px: 340px (было 300)
  // // - Иначе (>= 1400px): 400px
  // const theme = useTheme();
  // const isLessThan1100 = useMediaQuery(theme.breakpoints.down(1100));
  // const isBetween1100And1280 = useMediaQuery(theme.breakpoints.between(1100, 1280));
  // const isBetween1280And1400 = useMediaQuery(theme.breakpoints.between(1280, 1400));
  // const rightColumnWidth = isLessThan1100
  //   ? 280
  //   : isBetween1100And1280
  //     ? 320
  //     : isBetween1280And1400
  //       ? 340
  //       : 400;

  const rightColumnWidth = isLargeScreen ? 400 : 280;

  // Рефы для синхронизации между схемой и списком
  const schemaContainerRef = useRef<SchemaContainerRef>(null);
  const reservesListRef = useRef<ReservesListContainerRef>(null);

  // Состояние для отслеживания времени - обновляется каждую минуту
  // для оптимизации производительности (вместо каждых 5 секунд)
  // Функции getReserveItemTimeStatus и getReserveItemState используют текущее время напрямую,
  // поэтому это нужно только для триггера пересчета tablesSchedule
  const [currentTimeKey, setCurrentTimeKey] = useState(0);

  useEffect(() => {
    // Обновляем реже (каждую минуту) для оптимизации производительности
    // Статусы времени вычисляются динамически при каждом render,
    // поэтому редкое обновление не влияет на точность отображения
    const interval = setInterval(() => {
      setCurrentTimeKey((prev) => prev + 1);
    }, 60000); // 1 минута вместо 5 секунд

    return () => clearInterval(interval);
  }, []);

  const [cancelConfirmationForReserve, setCancelConfirmationForReserve] =
    useState<GM.ReservesSchemas.ScheduleReserve | null>(null);

  const [guestLeftConfirmationForReserve, setGuestLeftConfirmationForReserve] =
    useState<GM.ReservesSchemas.ScheduleReserve | null>(null);

  const [guestLeftConfirmationForBanquet, setGuestLeftConfirmationForBanquet] =
    useState<GM.ReservesSchemas.ScheduleReserve | null>(null);

  const [legendOpen, setLegendOpen] = useState(false);
  const [openedReserveDetail, setOpenedReserveDetail] =
    useState<GM.ReservesSchemas.ScheduleReserve | null>(null);

  // Сбрасываем openedReserveDetail при переходе с мобильного на средний/десктопный экран
  useEffect(() => {
    if (!isMobilePortrait && openedReserveDetail) {
      setOpenedReserveDetail(null);
    }
  }, [isMobilePortrait, openedReserveDetail]);

  // const cancelConfirmationMessages = useMemo(() => {
  //   if (cancelConfirmationForReserve) {
  //     return getCancelConfirmationMessages(cancelConfirmationForReserve);
  //   }
  //
  //   return null;
  // }, [cancelConfirmationForReserve]);

  const history = useHistory();
  let isError = false;
  const params = useParams<{
    date?: string;
    reserveOrTable?: string;
  }>();

  const restaurant = useRestaurant();
  const restaurantTz = restaurant?.info?.timeZoneOffset || undefined;

  // const now = toTzMoment(undefined, restaurantTz);
  const now = moment();
  const today = now.clone().startOf('day');
  const todayTs = today.valueOf();
  // const nowFormat = 'YYYY-MM-DD HH:mm';
  // const nowStr = now.format(nowFormat);

  let dateTs = todayTs;
  let pathDateTs = dateTs;
  if (params.date) {
    const pathDate = moment(params.date, 'YYYY-MM-DD', true);
    if (pathDate.isValid()) {
      pathDateTs = pathDate.startOf('day').valueOf();
      dateTs = pathDateTs;
    } else {
      isError = true;
    }
  }

  const [isDateChanging, setIsDateChanging] = useState(false);
  useEffect(() => setIsDateChanging(true), [dateTs]);

  const debouncedDate = useDebounce(dateTs, 500);
  useEffect(() => setIsDateChanging(false), [debouncedDate]);

  const date = moment(debouncedDate);
  const isToday = date.isSame(today, 'day');
  // const todayStr = today.format('YYYY-MM-DD');
  const { from, till } = useGetBookingRequestRange(debouncedDate || todayTs);
  const activeBooking = Booking.useActiveInPeriod(from, till);
  // const cancelReserve = Booking.useCancelReserveMutation(from, till);
  const cancelReserve = Booking.useCancelReserveMutation(from);
  const guestArrived = Booking.useGuestArrivedMutation(from);
  const guestLeft = Booking.useGuestLeftMutation(from);
  const activeOrders = Orders.useActiveInPeriod(from, till);
  const isLoading =
    isDateChanging ||
    (!activeBooking.isFetched && activeBooking.isLoading) ||
    (!activeOrders.isFetched && activeOrders.isLoading);

  const sections = useSections();
  const [sectionId, setSectionId] = useState<number | null>(null);

  const { reserve, table, section, schemaSelectedTableIds } = useMemo(() => {
    let reserve: GM.Booking.BookingDTO | undefined;
    let table: GM.ReservesSchemas.ReserveTableDTO | undefined;
    let section: GM.ReservesSchemas.SectionDTO | undefined;
    let schemaSelectedTableIds: number[] = [];

    // r-123456 or t-123456
    // Приоритет: если есть резерв/стол в URL, используем его зал (даже если был ручной выбор)
    if (params.reserveOrTable) {
      const reserveOrTable = params.reserveOrTable.split('-');
      if (reserveOrTable.length === 2) {
        const id = parseInt(reserveOrTable[1], 10);
        if (!isNaN(id) && id > 0) {
          if (reserveOrTable[0] === 'r') {
            reserve = activeBooking.data?.find((r) => r.id === id);
            if (reserve) {
              schemaSelectedTableIds = reserve.tables.map((t) => t.id);
              const sectionFromReserve = sections.data?.find((s) => {
                return s.tables.filter((t) => schemaSelectedTableIds.includes(t.id)).length > 0;
              });
              // Всегда используем section из резерва (приоритет над ручным выбором)
              if (sectionFromReserve) {
                section = sectionFromReserve;
              }
            }
          } else if (reserveOrTable[0] === 't') {
            sections.data?.forEach((s) => {
              const foundTable = s.tables.find((t) => t.id === id);
              if (foundTable) {
                table = foundTable;
                // Всегда используем section из стола (приоритет над ручным выбором)
                section = s;
                schemaSelectedTableIds = [table.id];
              }
            });
          }
        }
      }
    }

    // Если пользователь выбрал зал вручную (sectionId установлен), используем его
    // Только если нет активного резерва/стола в URL
    if (!section && sectionId && sections.data) {
      const selectedSection = sections.data.find((s) => s.schema && s.id === sectionId);
      if (selectedSection) {
        section = selectedSection;
      }
    }

    // Если section еще не установлен, используем первый зал со схемой
    if (!section && sections.data) {
      section = sections.data.find((s) => s.schema);
    }

    return { reserve, table, section, schemaSelectedTableIds };
  }, [params.reserveOrTable, activeBooking.data, sections.data, sectionId]);

  const isTableView = !!table;

  const restaurantId = useRestaurantId();
  const prevRestaurantIdRef = useRef(restaurantId);

  // Флаг для отслеживания ручного выбора зала
  const manualSectionSelectionRef = useRef(false);

  useEffect(() => {
    // Если есть резерв/стол в URL, всегда синхронизируем sectionId с section из URL
    // (это переключает зал при клике на резерв из списка)
    if (params.reserveOrTable && section?.id) {
      // Сбрасываем флаг ручного выбора при навигации через URL
      if (section.id !== sectionId) {
        manualSectionSelectionRef.current = false;
        setSectionId(section.id);
      }
    }
    // Устанавливаем sectionId из section только если он еще не установлен вручную
    // Это происходит при первой загрузке или когда section меняется из-за URL параметров
    else if (section?.id && !sectionId && !manualSectionSelectionRef.current) {
      setSectionId(section.id);
    }
    // Если sectionId установлен, но section не соответствует ему (например, из-за URL),
    // и нет активного резерва/стола в URL, и не было ручного выбора, то обновляем sectionId
    else if (
      section?.id &&
      sectionId &&
      section.id !== sectionId &&
      !params.reserveOrTable &&
      !manualSectionSelectionRef.current
    ) {
      setSectionId(section.id);
    }
    // Сбрасываем флаг ручного выбора, если sectionId совпадает с section
    if (section?.id && sectionId === section.id) {
      manualSectionSelectionRef.current = false;
    }
  }, [section?.id, sectionId, params.reserveOrTable]);

  if (params.reserveOrTable && !reserve && !table) {
    isError = true;
  }

  const schedule = useMemo<
    Array<GM.ReservesSchemas.ScheduleReserve | GM.ReservesSchemas.ScheduleOrder>
  >(() => {
    if (isError && !isLoading) return [];

    const mapReserve = (b: GM.Booking.BookingDTO): GM.ReservesSchemas.ScheduleReserve => {
      return {
        ...b,
        scheduleType: 'RESERVE',
      };
    };

    const mapOrder = (o: GM.Orders.OrderDTO): GM.ReservesSchemas.ScheduleOrder => {
      return {
        ...o,
        scheduleType: 'ORDER',
      };
    };

    const reserves = (activeBooking?.data ?? []).map(mapReserve);
    const orders = (activeOrders?.data ?? []).map(mapOrder);

    return [...reserves, ...orders].sort((a, b) =>
      a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
    );
  }, [isError, isLoading, activeBooking?.data, activeOrders?.data]);

  const tablesSchedule = useMemo(() => {
    if (isError && !isLoading) {
      return {};
    }

    const tables: GM.ReservesSchemas.TablesSchedule = {};

    schedule.forEach((r) => {
      r.tables.forEach((t) => {
        if (tables[t.id]) {
          if (isTableOccupied(r)) {
            tables[t.id].orderTypeItems.push(r);
          }

          if (r.scheduleType === 'RESERVE' && r.isScheduled) {
            tables[t.id].reserves.push(r);
          }
        } else {
          const initTable = {
            table: t,
            reserves: [],
            orderTypeItems: [],
            current: null,
            next: null,
          };

          if (isTableOccupied(r)) {
            initTable.orderTypeItems.push(r);
          }

          if (r.scheduleType === 'RESERVE' && r.isScheduled) {
            initTable.reserves.push(r);
          }

          tables[t.id] = initTable;
        }

        const nextOccupiedReserve = tables[t.id].reserves.find((item) => isTableOccupied(item));

        if (!tables[t.id].current && tables[t.id].orderTypeItems.length > 0) {
          tables[t.id].current = tables[t.id].orderTypeItems[0];
        } else if (!tables[t.id].current && nextOccupiedReserve) {
          tables[t.id].current = nextOccupiedReserve;
        } else if (!tables[t.id].next) {
          tables[t.id].next = r;
        }
      });
    });

    Object.keys(tables).forEach((_id) => {
      const id = parseInt(_id, 10);
      const { current, next } = tables[id];

      const stateV2 =
        current || next ? getReserveItemState(current || next, tables[id], restaurantTz) : 'FREE';

      const attributes = {
        state: 'FREE',
        reserveTimeStr: undefined,
        reserveTimeStatus: undefined,
        forcedTableTimeStatus: undefined,
        stateV2,
      } as GM.ReservesSchemas.TableSchedule['attributes'];

      if (current) {
        if (current.state === 'PRE_CHECK') {
          attributes.state = 'PRE_CHECK';
        } else if (isTableOpen(current)) {
          attributes.state = 'OPEN';
        } else if (isTableOccupied(current)) {
          attributes.state = 'ARRIVED';
        }
      }

      let significantReserveTime: { status: GM.ReservesSchemas.TimeStatus; time: string } =
        undefined;

      if (current && current.scheduleType === 'RESERVE' && current.state === 'ARRIVED') {
        if (current.isScheduled) {
          // Исключение для занятого стола по резерву без открытия заказа:
          // бейдж с временем нужно выводить синий и время должно
          // быть текущего резерва
          significantReserveTime = getReserveItemTimeStatus(current, restaurantTz);

          // А цвет стола должен быть желтым или красным в зависимости
          // от следующего резерва
          if (next) {
            const nextTime = getReserveItemTimeStatus(next, restaurantTz);
            if (['SOON', 'DUE'].includes(nextTime.status)) {
              attributes.forcedTableTimeStatus = nextTime.status;
            }
          }
        } else if (next) {
          // Исключение для занятого стола БЕЗ резерва без открытия заказа:
          // сам стол синий, бейдж с временем относится к следующему резерву
          // с учетом статуса следующего резерва
          significantReserveTime = getReserveItemTimeStatus(next, restaurantTz);

          if (['SOON', 'DUE'].includes(significantReserveTime.status)) {
            attributes.forcedTableTimeStatus = 'ARRIVED';
          }
        }
      } else if (next) {
        significantReserveTime = getReserveItemTimeStatus(next, restaurantTz);
      }

      if (significantReserveTime) {
        attributes.reserveTimeStr = significantReserveTime.time;
        attributes.reserveTimeStatus = significantReserveTime.status;
      }

      tables[id].attributes = attributes;
    });

    return tables;

    // currentTimeKey нужно, чтобы обновлять расписание столов в зависимости от текущего времени
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isLoading, schedule, restaurantTz, currentTimeKey]);

  const reservesView = useMemo<Array<GM.ReservesSchemas.ScheduleReserve>>(() => {
    if (table?.id) {
      if (tablesSchedule[table.id]) {
        return tablesSchedule[table.id].reserves.filter(
          (item): item is GM.ReservesSchemas.ScheduleReserve =>
            item.id !== tablesSchedule[table.id].current?.id && !isTableOccupied(item),
        );
      }

      return [];
    }
    return schedule.filter(
      (item): item is GM.ReservesSchemas.ScheduleReserve =>
        item.scheduleType === 'RESERVE' && item.isScheduled && !isTableOccupied(item),
    );
  }, [table?.id, schedule, tablesSchedule]);

  const gotoRootRespectingDate = useCallback(
    (payload: { tableId: number } | { reserveId: number } | null) => {
      let newPath = URI;
      let newParams: string[] = [];

      if (payload) {
        if ('tableId' in payload && payload.tableId) {
          newParams.push(`t-${payload.tableId}`);
        } else if ('reserveId' in payload && payload.reserveId) {
          newParams.push(`r-${payload.reserveId}`);
        }
      }

      if (debouncedDate && (newParams.length > 0 || !isToday)) {
        const date = moment(debouncedDate);

        newParams = [date.format('YYYY-MM-DD'), ...newParams];

        newPath += '/' + newParams.join('/');
      }

      if (history.location.pathname !== newPath) {
        history.push(newPath);
      }
    },
    [debouncedDate, isToday, history],
  );

  const handleChangeDate = useCallback(
    (nextDate: moment.Moment | null) => {
      let newPath = URI;
      const newParams: string[] = [];

      if (nextDate && nextDate.isBefore(today)) {
        return;
      }

      if (nextDate && !nextDate.isSame(today, 'day')) {
        newParams.push(nextDate.format('YYYY-MM-DD'));
      } else if (table) {
        newParams.push(today.format('YYYY-MM-DD'));
      }

      if (table) {
        newParams.push(`t-${table.id}`);
      }

      if (newParams.length > 0) {
        newPath += '/' + newParams.join('/');
      }

      history.push(newPath);
    },
    [table, today, history],
  );

  useEffect(() => {
    if (prevRestaurantIdRef.current !== restaurantId) {
      gotoRootRespectingDate(null);
    }

    prevRestaurantIdRef.current = restaurantId;
  }, [restaurantId, gotoRootRespectingDate]);

  useEffect(() => {
    if (isError && params.reserveOrTable && !isLoading) {
      gotoRootRespectingDate(null);
    }
  }, [isError, isLoading, params.reserveOrTable, gotoRootRespectingDate]);

  useEffect(() => {
    if (cancelReserve.isError || cancelReserve.isSuccess) {
      setCancelConfirmationForReserve(null);
      setGuestLeftConfirmationForReserve(null);
      setGuestLeftConfirmationForBanquet(null);
    }

    if (cancelReserve.isSuccess && !isTableView) {
      gotoRootRespectingDate(null);
    }
  }, [cancelReserve.isSuccess, cancelReserve.isError, gotoRootRespectingDate, isTableView]);

  useEffect(() => {
    if (guestLeft.isError || guestLeft.isSuccess) {
      setGuestLeftConfirmationForReserve(null);
      setCancelConfirmationForReserve(null);
      setGuestLeftConfirmationForBanquet(null);
    }
  }, [guestLeft.isSuccess, guestLeft.isError]);

  const [openedReserveIds, setOpenedReserveIds] = useState<number[]>([]);
  const [forceClosedReserveIds, setForceClosedReserveIds] = useState<number[]>([]);

  const toggleOpenedReserveId = (id: number) => {
    const isOpenedNow = openedReserveIds.includes(id);

    setForceClosedReserveIds((prev) => {
      if (!isOpenedNow) {
        return prev.filter((rid) => rid !== id);
      }

      return [...prev, id];
      // return [id];
    });

    setOpenedReserveIds((prev) => {
      if (isOpenedNow) {
        return prev.filter((rid) => rid !== id);
      }

      // return [...prev, id];
      return [id];
    });
  };

  useEffect(() => {
    if (isLoading || !table || reservesView.length === 0) {
      return;
    }

    const isOpenedReserve = reservesView.some((reserve) => openedReserveIds.includes(reserve.id));

    if (!isOpenedReserve && !forceClosedReserveIds.includes(reservesView[0].id)) {
      // setOpenedReserveIds((prev) => [...prev, reservesView[0].id]);
      setOpenedReserveIds([reservesView[0].id]);
    }
  }, [isLoading, openedReserveIds, forceClosedReserveIds, table, reservesView]);

  if (isError && !isLoading) {
    return <NotFoundPage />;
  }

  return (
    <Box
      className="design-v3-font"
      display="flex"
      flexDirection="row"
      height="100%"
      style={{
        overflow: 'hidden',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {cancelConfirmationForReserve && cancelConfirmationForReserve.isScheduled && (
        <>
          {isMobile ? (
            <ConfirmationBottomSheet
              open
              onClose={() => setCancelConfirmationForReserve(null)}
              onConfirm={() => cancelReserve.mutate(cancelConfirmationForReserve.id)}
              confirmButtonLabel="Да, отменить резерв"
              cancelButtonLabel="Не отменять"
            >
              <Box mb="16px">Вы действительно хотите отменить резерв?</Box>
              <ReserveInfo
                reserve={cancelConfirmationForReserve}
                restaurantTz={restaurantTz}
                state={getReserveItemState(
                  cancelConfirmationForReserve,
                  tablesSchedule[cancelConfirmationForReserve.tables[0].id],
                  restaurantTz,
                )}
              />
            </ConfirmationBottomSheet>
          ) : (
            <Modal
              open
              onClose={() => setCancelConfirmationForReserve(null)}
              onConfirm={() => cancelReserve.mutate(cancelConfirmationForReserve.id)}
              confirmButtonLabel="Да, отменить резерв"
              cancelButtonLabel="Не отменять"
            >
              <Box>Вы действительно хотите отменить резерв?</Box>

              <ReserveInfo
                reserve={cancelConfirmationForReserve}
                restaurantTz={restaurantTz}
                // TODO Тут и ниже в модалках не нравится определение state. Отрефакторить
                state={getReserveItemState(
                  cancelConfirmationForReserve,
                  tablesSchedule[cancelConfirmationForReserve.tables[0].id],
                  restaurantTz,
                )}
              />
            </Modal>
          )}
        </>
      )}

      {/* В оригинальном сообщении перечисление столов */}
      {cancelConfirmationForReserve && !cancelConfirmationForReserve.isScheduled && (
        <>
          {isMobile ? (
            <ConfirmationBottomSheet
              open
              onClose={() => setCancelConfirmationForReserve(null)}
              onConfirm={() => cancelReserve.mutate(cancelConfirmationForReserve.id)}
              confirmButtonLabel="Да, освободить стол"
              cancelButtonLabel="Не освобождать"
            >
              <Box>Вы действительно хотите освободить стол?</Box>
            </ConfirmationBottomSheet>
          ) : (
            <Modal
              open
              onClose={() => setCancelConfirmationForReserve(null)}
              onConfirm={() => cancelReserve.mutate(cancelConfirmationForReserve.id)}
              confirmButtonLabel="Да, освободить стол"
              cancelButtonLabel="Не освобождать"
            >
              <Box>Вы действительно хотите освободить стол?</Box>
            </Modal>
          )}
        </>
      )}

      {guestLeftConfirmationForReserve && (
        <>
          {isMobile ? (
            <ConfirmationBottomSheet
              open
              cancelButtonLabel="Отмена"
              onClose={() => setGuestLeftConfirmationForReserve(null)}
              confirmButtonLabel="Освободить стол"
              onConfirm={() => {
                guestLeft.mutate(guestLeftConfirmationForReserve.id);
              }}
              optionButtonLabel="Освободить и отменить резерв"
              onOption={() => {
                cancelReserve.mutate(guestLeftConfirmationForReserve.id);
              }}
              optionButtonStyle={{
                background: button.primary.neutral.bg,
                color: button.primary.neutral.label,
              }}
            >
              {isTableOccupied(guestLeftConfirmationForReserve) ? (
                <>
                  <Box mb="16px">Стол занят гостем по&nbsp;резерву.</Box>
                  <Box mb="16px">Освободить стол или&nbsp;освободить и&nbsp;отменить резерв?</Box>
                </>
              ) : (
                <Box mb="16px">Резерв будет отменён. Продолжить?</Box>
              )}

              <ReserveInfo
                reserve={guestLeftConfirmationForReserve}
                restaurantTz={restaurantTz}
                state={getReserveItemState(
                  guestLeftConfirmationForReserve,
                  tablesSchedule[guestLeftConfirmationForReserve.tables[0].id],
                  restaurantTz,
                )}
              />
            </ConfirmationBottomSheet>
          ) : (
            <Modal
              open
              cancelButtonLabel="Отмена"
              onClose={() => setGuestLeftConfirmationForReserve(null)}
              confirmButtonLabel="Освободить стол"
              onConfirm={() => {
                guestLeft.mutate(guestLeftConfirmationForReserve.id);
              }}
              optionButtonLabel="Освободить и отменить резерв"
              onOption={() => {
                cancelReserve.mutate(guestLeftConfirmationForReserve.id);
              }}
              optionButtonStyle={{
                background: button.primary.neutral.bg,
                color: button.primary.neutral.label,
                // padding: '0 30px',
              }}
            >
              {isTableOccupied(guestLeftConfirmationForReserve) ? (
                <>
                  <Box mb="20px">Стол занят гостем по&nbsp;резерву.</Box>
                  <Box>Освободить стол или&nbsp;освободить и&nbsp;отменить резерв?</Box>
                </>
              ) : (
                <Box>Резерв будет отменён. Продолжить?</Box>
              )}

              <ReserveInfo
                reserve={guestLeftConfirmationForReserve}
                restaurantTz={restaurantTz}
                state={getReserveItemState(
                  guestLeftConfirmationForReserve,
                  tablesSchedule[guestLeftConfirmationForReserve.tables[0].id],
                  restaurantTz,
                )}
              />
            </Modal>
          )}
        </>
      )}

      {guestLeftConfirmationForBanquet && (
        <>
          {isMobile ? (
            <ConfirmationBottomSheet
              open
              cancelButtonLabel="Отмена"
              onClose={() => setGuestLeftConfirmationForBanquet(null)}
              confirmButtonLabel="Освободить стол"
              onConfirm={() => {
                guestLeft.mutate(guestLeftConfirmationForBanquet.id);
              }}
            >
              <Box mb="16px">Стол сейчас занят под&nbsp;банкет.</Box>
              <Box mb="16px">
                Стол можно освободить здесь, а&nbsp;отмена банкета выполняется на&nbsp;кассе.
              </Box>

              <ReserveInfo
                reserve={guestLeftConfirmationForBanquet}
                restaurantTz={restaurantTz}
                state={getReserveItemState(
                  guestLeftConfirmationForBanquet,
                  tablesSchedule[guestLeftConfirmationForBanquet.tables[0].id],
                  restaurantTz,
                )}
              />
            </ConfirmationBottomSheet>
          ) : (
            <Modal
              open
              cancelButtonLabel="Отмена"
              onClose={() => setGuestLeftConfirmationForBanquet(null)}
              confirmButtonLabel="Освободить стол"
              onConfirm={() => {
                guestLeft.mutate(guestLeftConfirmationForBanquet.id);
              }}
            >
              <Box mb="20px">Стол сейчас занят под&nbsp;банкет.</Box>
              <Box>
                Стол можно освободить здесь, а&nbsp;отмена банкета выполняется на&nbsp;кассе.
              </Box>

              <ReserveInfo
                reserve={guestLeftConfirmationForBanquet}
                restaurantTz={restaurantTz}
                state={getReserveItemState(
                  guestLeftConfirmationForBanquet,
                  tablesSchedule[guestLeftConfirmationForBanquet.tables[0].id],
                  restaurantTz,
                )}
              />
            </Modal>
          )}
        </>
      )}

      <Box
        flexGrow={1}
        flexShrink={1}
        minWidth={0}
        minHeight={0}
        bgcolor="#FFFFFF"
        paddingLeft={isMobilePortrait ? '12px' : isMobile ? '8px' : '24px'}
        paddingRight={isMobilePortrait ? '12px' : isMobile ? '12px' : '24px'}
        paddingTop={isMobilePortrait ? '8px' : isMobile ? '8px' : '20px'}
        paddingBottom={
          isMobilePortrait ? 'calc(128px + env(safe-area-inset-bottom))' : isMobile ? '8px' : '40px'
        }
        height="100%"
        style={{ overflow: 'hidden', boxSizing: 'border-box' }}
        display="flex"
        flexDirection="column"
      >
        {sections.data && sections.data.length > 0 && (
          <Box
            flexGrow={1}
            flexShrink={1}
            style={{ minHeight: 0, minWidth: 0, height: '100%', overflow: 'hidden' }}
          >
            <SchemaWidget
              ref={schemaContainerRef}
              sections={sections.data}
              onSelectSection={(nextSectionId) => {
                // Устанавливаем флаг ручного выбора
                manualSectionSelectionRef.current = true;
                // Устанавливаем sectionId напрямую при ручном выборе
                setSectionId(nextSectionId);
                // Сбрасываем выбранный стол/резерв при смене зала
                if (table || reserve) {
                  gotoRootRespectingDate(null);
                }
              }}
              sectionId={sectionId}
              selectedTableIds={schemaSelectedTableIds}
              onClickSchema={() => {
                if (table || reserve) {
                  gotoRootRespectingDate(null);
                }
              }}
              onClickTable={(id: number) => {
                // Найти резерв, связанный со столом
                const relatedReserve = reservesView.find((r) => r.tables.some((t) => t.id === id));

                // Прокрутить к резерву в списке, если он найден
                if (relatedReserve && reservesListRef.current) {
                  reservesListRef.current.scrollToReserve(relatedReserve.id);
                }

                if (table?.id !== id) {
                  gotoRootRespectingDate({ tableId: id });
                } else {
                  gotoRootRespectingDate(null);
                }
              }}
              tablesSchedule={tablesSchedule}
              // offsetWidth={isMobilePortrait ? 0 : isDesktop ? rightColumnWidth : 0}
              offsetWidth={rightColumnWidth}
              date={pathDateTs}
              onChangeDate={handleChangeDate}
            />
          </Box>
        )}

        {/* Горизонтальная лента резервов для мобильных портретов */}
        {isMobilePortrait && reservesView.length > 0 && (
          <ReservesHorizontalList
            reservesView={reservesView}
            tablesSchedule={tablesSchedule}
            restaurantTz={restaurantTz}
            selectedReserveId={reserve?.id}
            onReserveClick={(reserveId) => {
              const clickedReserve = reservesView.find((r) => r.id === reserveId);
              if (clickedReserve) {
                // Фокусируем стол на схеме
                if (clickedReserve.tables.length > 0) {
                  const tableId = clickedReserve.tables[0].id;
                  if (schemaContainerRef.current) {
                    schemaContainerRef.current.focusTable(tableId);
                  }
                }
                gotoRootRespectingDate({ reserveId });
              }
            }}
            onCancel={(scheduleReserve) => {
              if (scheduleReserve.isBanquet) {
                setGuestLeftConfirmationForBanquet(scheduleReserve);
              } else if (scheduleReserve.isScheduled) {
                setCancelConfirmationForReserve(scheduleReserve);
              } else {
                setCancelConfirmationForReserve(scheduleReserve);
              }
            }}
            isCancelDisabled={guestLeft.isLoading}
            isLoading={isLoading}
          />
        )}
      </Box>

      {/* Вертикальный список для >=600px (компактный для 600-900px, обычный для >=900px) */}
      {!isMobilePortrait && (
        <Box
          flexGrow={0}
          flexShrink={0}
          minWidth={0}
          // width={isMobile && !isMobilePortrait ? 280 : rightColumnWidth}
          width={rightColumnWidth}
          height="100%"
          bgcolor="#FFFFFF"
          display="flex"
          flexDirection="column"
          style={{ overflow: 'hidden', boxSizing: 'border-box' }}
        >
          <Box
            flexGrow={0}
            flexShrink={0}
            style={{
              paddingTop: isLargeScreen ? '20px' : '8px',
              paddingRight: isLargeScreen ? '24px' : '12px',
              paddingBottom: '24px',
              paddingLeft: '8px',
            }}
          >
            <CalendarWidget date={pathDateTs} onChangeDate={handleChangeDate} />
          </Box>

          {isLoading && (
            <Box textAlign="center" marginRight="24px">
              <CircularProgress color="primary" />
            </Box>
          )}

          {!isLoading && isTableView && (
            <Box
              flexGrow={0}
              flexShrink={0}
              // paddingX={isMediumScreen ? '0px' : '20px'}
              // paddingLeft={isMediumScreen ? '8px' : undefined}
              // paddingRight={isMediumScreen ? '12px' : undefined}
              // paddingY={isMediumScreen ? '8px' : undefined}
              style={{
                paddingRight: isLargeScreen ? '24px' : '12px',
                paddingLeft: '8px',
              }}
            >
              <TableWidget
                table={table}
                onBack={() => gotoRootRespectingDate(null)}
                isCancelDisabled={cancelReserve.isLoading}
                onCancel={(scheduleReserve: GM.ReservesSchemas.ScheduleReserve) => {
                  if (scheduleReserve.isBanquet) {
                    setGuestLeftConfirmationForBanquet(scheduleReserve);
                  } else if (scheduleReserve.isScheduled) {
                    setGuestLeftConfirmationForReserve(scheduleReserve);
                  } else {
                    setCancelConfirmationForReserve(scheduleReserve);
                  }
                }}
                isGuestArrivedDisabled={guestArrived.isLoading}
                onGuestArrived={isToday ? (payload) => guestArrived.mutate(payload) : undefined}
                tableSchedule={tablesSchedule[table.id]}
                restaurantTz={restaurantTz}
                // compact={isMediumScreen}
              />
              <HR margin={24} size={2} />
            </Box>
          )}

          <Box
            flexGrow={1}
            flexShrink={1}
            minHeight={0}
            style={{
              overflowY: 'auto',
              width: '100%',
              minWidth: 0,

              // paddingTop: '0',
              // paddingRight: isMobile ? '12px' : '24px',
              // paddingBottom: '0',
              // paddingLeft: isMobile ? '8px' : '20px',
              // backgroundColor: 'rgb(255, 255, 255)',

              paddingRight: isLargeScreen ? '24px' : '12px',
              paddingLeft: '8px',
              paddingBottom: '20px',
            }}
          >
            <ReservesListContainer
              ref={reservesListRef}
              reservesView={reservesView}
              tablesSchedule={tablesSchedule}
              restaurantTz={restaurantTz}
              selectedReserveId={reserve?.id}
              isTableView={isTableView}
              onReserveExpand={(reserveId) => {
                const clickedReserve = reservesView.find((r) => r.id === reserveId);
                if (clickedReserve) {
                  if (table) {
                    toggleOpenedReserveId(reserveId);
                  } else {
                    if (clickedReserve.tables.length > 0) {
                      const tableId = clickedReserve.tables[0].id;
                      if (schemaContainerRef.current) {
                        schemaContainerRef.current.focusTable(tableId);
                      }
                    }
                    gotoRootRespectingDate({ reserveId });
                  }
                }
              }}
              onReserveCollapse={(reserveId) => {
                if (table) {
                  toggleOpenedReserveId(reserveId);
                } else {
                  gotoRootRespectingDate(null);
                }
              }}
              onCancel={(scheduleReserve) => {
                if (scheduleReserve.isBanquet) {
                  setGuestLeftConfirmationForBanquet(scheduleReserve);
                } else if (scheduleReserve.isScheduled) {
                  setCancelConfirmationForReserve(scheduleReserve);
                } else {
                  setCancelConfirmationForReserve(scheduleReserve);
                }
              }}
              isCancelDisabled={guestLeft.isLoading}
              isLoading={isLoading}
              renderEmpty={() => (
                <Box
                  // fontSize={isMediumScreen ? 12 : 14}
                  fontSize={14}
                  color={fg.muted}
                  // marginBottom={isMediumScreen ? '20px' : '40px'}
                  marginBottom={'40px'}
                >
                  {isTableView ? 'Резервов на этом столе нет' : 'Нет резервов'}
                </Box>
              )}
              // compact={isMediumScreen}
            />
          </Box>
        </Box>
      )}

      {isMobilePortrait && (
        <SwipeableDrawer
          anchor="bottom"
          open={!!table}
          onClose={() => gotoRootRespectingDate(null)}
          onOpen={() => {
            // Пустая функция для onOpen, так как открытие происходит только по клику на стол
          }}
          className="design-v3-font"
          disableSwipeToOpen={true}
          variant="temporary"
          PaperProps={{
            style: {
              maxHeight: 'calc(85vh - env(safe-area-inset-bottom))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              paddingBottom: 'env(safe-area-inset-bottom)',
            },
          }}
          BackdropProps={{
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          <Box
            paddingX="20px"
            paddingTop="12px"
            paddingBottom="max(24px, env(safe-area-inset-bottom))"
            style={{
              overflowY: 'auto',
              maxHeight: 'calc(85vh - env(safe-area-inset-bottom) - 60px)',
            }}
          >
            <Box
              width={40}
              height={4}
              borderRadius={2}
              bgcolor="rgba(0,0,0,0.2)"
              marginX="auto"
              marginBottom="16px"
            />

            {isLoading && (
              <Box textAlign="center" paddingY="24px">
                <CircularProgress color="primary" />
              </Box>
            )}

            {!isLoading && table && (
              <>
                <TableWidget
                  table={table}
                  onBack={() => gotoRootRespectingDate(null)}
                  isCancelDisabled={cancelReserve.isLoading}
                  onCancel={(scheduleReserve: GM.ReservesSchemas.ScheduleReserve) => {
                    if (scheduleReserve.isBanquet) {
                      setGuestLeftConfirmationForBanquet(scheduleReserve);
                    } else if (scheduleReserve.isScheduled) {
                      setGuestLeftConfirmationForReserve(scheduleReserve);
                    } else {
                      setCancelConfirmationForReserve(scheduleReserve);
                    }
                  }}
                  isGuestArrivedDisabled={guestArrived.isLoading}
                  onGuestArrived={isToday ? (payload) => guestArrived.mutate(payload) : undefined}
                  tableSchedule={tablesSchedule[table.id]}
                  restaurantTz={restaurantTz}
                />
                <HR margin={24} size={2} />
              </>
            )}

            {!isLoading && table && !reservesView?.length && (
              <Box fontSize={14} color={fg.muted} marginBottom="40px">
                Резервов на этом столе нет
              </Box>
            )}

            {!isLoading &&
              table &&
              reservesView.map((scheduleItem, i) => {
                const hr = i > 0 ? <HR margin={24} /> : null;

                if (
                  scheduleItem.id === reserve?.id ||
                  (!forceClosedReserveIds.includes(scheduleItem.id) &&
                    openedReserveIds.includes(scheduleItem.id))
                ) {
                  return (
                    <Fragment key={scheduleItem.id}>
                      {hr}
                      <OpenedReserveWidget
                        onCollapse={() => toggleOpenedReserveId(scheduleItem.id)}
                        onCancel={
                          !scheduleItem.isBanquet && !scheduleItem.openedTableDate
                            ? () => setCancelConfirmationForReserve(scheduleItem)
                            : undefined
                        }
                        isCancelDisabled={guestLeft.isLoading}
                        scheduleReserve={scheduleItem}
                        tablesSchedule={tablesSchedule}
                        restaurantTz={restaurantTz}
                      />
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={scheduleItem.id}>
                    {hr}
                    <ReserveWidget
                      onExpand={() => toggleOpenedReserveId(scheduleItem.id)}
                      scheduleReserve={scheduleItem}
                      tablesSchedule={tablesSchedule}
                      restaurantTz={restaurantTz}
                    />
                  </Fragment>
                );
              })}
          </Box>
        </SwipeableDrawer>
      )}

      {isMobile && <LegendBottomSheet open={legendOpen} onClose={() => setLegendOpen(false)} />}
    </Box>
  );
};
